import PageHeader from '../components/ui/PageHeader'
import AddIcon from '@offline-mui/icons-material/Add'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TablePagination,
} from '@offline-mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from '../router'
import { deleteFlightsData, fetchFlightsData } from '../api/inventory'
import type { FlightsData } from '../api/inventory'
import FlightDataAccordionItem from '../components/FlightDataAccordionItem'
import FlightDataDialog from '../components/FlightDataDialog'

export default function FlightsDataPage() {
  const [searchParams] = useSearchParams()
  const [rows, setRows] = useState<FlightsData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<FlightsData | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const openedQueryRef = useRef<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetchFlightsData()
      setRows(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flights')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const matchedFlight = useMemo(() => {
    const id = Number(searchParams.get('id'))
    const carrier = searchParams.get('carrier')?.toUpperCase()
    const flightNumber = searchParams.get('flightNumber')
    if (!id && !carrier && !flightNumber) return null
    return (
      (id ? rows.find((row) => row.id === id) : null) ||
      rows.find(
        (row) =>
          (!carrier || row.carrier.toUpperCase() === carrier) &&
          (!flightNumber || row.flight_number === flightNumber),
      ) ||
      null
    )
  }, [rows, searchParams])

  useEffect(() => {
    if (loading || !matchedFlight) return
    const queryKey = searchParams.toString()
    if (!queryKey || openedQueryRef.current === queryKey) return
    openedQueryRef.current = queryKey
    const index = rows.findIndex((row) => row.id === matchedFlight.id)
    if (index >= 0) setPage(Math.floor(index / rowsPerPage))
    setEditing(matchedFlight)
    setDialogOpen(true)
  }, [loading, matchedFlight, rows, rowsPerPage, searchParams])

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (flight: FlightsData) => {
    setEditing(flight)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditing(null)
  }

  const handleSaved = (flight: FlightsData) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row.id === flight.id)
      if (index >= 0) {
        const next = [...prev]
        next[index] = flight
        return next
      }
      return [...prev, flight]
    })
  }

  const handleDelete = async (flight: FlightsData) => {
    const confirmed = window.confirm(
      `Delete flight ${flight.carrier}${flight.flight_number} (${flight.departure_airport}→${flight.arrival_airport})?`,
    )
    if (!confirmed) return
    try {
      await deleteFlightsData(flight.id)
      setRows((prev) => prev.filter((row) => row.id !== flight.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Stack spacing={2}>
      <PageHeader
        eyebrow="Inventory"
        title="Flights"
        description="Manage dated flight legs stored in flights_data."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add flight
          </Button>
        }
      />

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Chip label={`${rows.length} flights`} size="small" />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">No flights yet. Add one or create a block.</Alert>
      ) : (
        <>
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            {pagedRows.map((flight) => (
              <FlightDataAccordionItem
                key={flight.id}
                flight={flight}
                defaultExpanded={matchedFlight?.id === flight.id}
                onEdit={openEdit}
                onDelete={(row) => void handleDelete(row)}
              />
            ))}
          </Paper>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}

      <FlightDataDialog
        open={dialogOpen}
        editing={editing}
        onClose={closeDialog}
        onSaved={handleSaved}
      />
    </Stack>
  )
}
